import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $getSelection } from 'lexical';
import { up } from 'styled-breakpoints';
import {
  ExternalIntegrationsNamespaceInv360CompanyEnabledArgs,
  ExternalIntegrationsNamespaceInv360GenerateShareLinkArgs,
  ExternalIntegrationsNamespaceInv360UsersArgs,
  Inv360VehicleObject,
  Mutation,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { RightDrawer } from 'src/components/pages/conversations/components/drawer/right-drawer';
import {
  BasePluginProps,
  PluginIcon,
} from 'src/components/pages/conversations/components/general/lexical-editor/plugins/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useVisible } from 'src/hooks/use-visible';
import {
  getLocalStorageItem,
  LOCAL_STORAGE_KEY_INV360_SALESPERSON_ID,
  removeLocalStorageItem,
  setLocalStorageItem,
} from 'src/utils/local-storage';
import { Inv360ConfigureStep } from './components/inv360-configure-step';
import { Inv360VehiclePicker } from './components/inv360-vehicle-picker';
import {
  INV360_COMPANY_ENABLED_QUERY,
  INV360_GENERATE_SHARE_LINK_MUTATION,
  INV360_USERS_QUERY,
  Inv360SortOption,
} from './operations';

type Step = 'picker' | 'configure';

export type Inv360WidgetPluginProps = BasePluginProps;

export const Inv360WidgetPlugin = memo<Inv360WidgetPluginProps>(({ disabled }) => {
  const { t } = useTranslation(['conversations']);
  const [editor] = useLexicalComposerContext();
  const visibility = useVisible(false);
  const breakpointUpMd = useBreakpoint(up('md'));
  const showNotification = useShowNotification();
  const { id: institutionId } = useCurrentInstitution();

  const { data: enabledData } = useQuery<
    Pick<Query, 'externalIntegrations'>,
    ExternalIntegrationsNamespaceInv360CompanyEnabledArgs
  >(INV360_COMPANY_ENABLED_QUERY, {
    variables: { institutionId },
  });

  const [generateShareLink, { loading: isGenerating }] = useMutation<
    Pick<Mutation, 'externalIntegrations'>,
    ExternalIntegrationsNamespaceInv360GenerateShareLinkArgs
  >(INV360_GENERATE_SHARE_LINK_MUTATION);

  const [step, setStep] = useState<Step>('picker');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Inv360SortOption>('LATEST');
  const [selectedVehiclesById, setSelectedVehiclesById] = useState<
    Map<number, Inv360VehicleObject>
  >(new Map());
  const [salespersonId, setSalespersonId] = useState<number | null>(() => {
    const stored = getLocalStorageItem(LOCAL_STORAGE_KEY_INV360_SALESPERSON_ID);
    const parsed = stored ? Number(stored) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });
  const [rememberSalesperson, setRememberSalesperson] = useState<boolean>(() => {
    const stored = getLocalStorageItem(LOCAL_STORAGE_KEY_INV360_SALESPERSON_ID);
    return stored !== null && Number.isFinite(Number(stored));
  });

  const { data: usersData } = useQuery<
    Pick<Query, 'externalIntegrations'>,
    ExternalIntegrationsNamespaceInv360UsersArgs
  >(INV360_USERS_QUERY, {
    variables: { input: { institutionId, perPage: 250 } },
    skip: !visibility.visible,
  });

  const salespersonOptions = useMemo(
    () =>
      (usersData?.externalIntegrations.inv360Users.data ?? []).map((user) => ({
        value: String(user.id),
        label: user.fullName,
      })),
    [usersData],
  );

  const handleClose = useCallback(() => {
    visibility.close();
    setStep('picker');
    setSearch('');
    setSelectedVehiclesById(new Map());
    if (!rememberSalesperson) {
      setSalespersonId(null);
    }
  }, [rememberSalesperson, visibility]);

  const handleRememberSalespersonChange = useCallback(
    (checked: boolean, currentSalespersonId: number | null) => {
      setRememberSalesperson(checked);
      if (!checked) {
        removeLocalStorageItem(LOCAL_STORAGE_KEY_INV360_SALESPERSON_ID);
      } else if (currentSalespersonId !== null) {
        setLocalStorageItem(
          LOCAL_STORAGE_KEY_INV360_SALESPERSON_ID,
          String(currentSalespersonId),
        );
      }
    },
    [],
  );

  const handleSalespersonChange = useCallback(
    (id: number | null) => {
      setSalespersonId(id);
      if (!rememberSalesperson) return;

      if (id === null) {
        removeLocalStorageItem(LOCAL_STORAGE_KEY_INV360_SALESPERSON_ID);
      } else {
        setLocalStorageItem(LOCAL_STORAGE_KEY_INV360_SALESPERSON_ID, String(id));
      }
    },
    [rememberSalesperson],
  );

  const toggleSelected = useCallback((vehicle: Inv360VehicleObject) => {
    setSelectedVehiclesById((prev) => {
      const next = new Map(prev);
      if (next.has(vehicle.id)) {
        next.delete(vehicle.id);
      } else {
        next.set(vehicle.id, vehicle);
      }
      return next;
    });
  }, []);

  const removeVehicle = useCallback((id: number) => {
    setSelectedVehiclesById((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const selectedVehicles = useMemo(
    () => Array.from(selectedVehiclesById.values()),
    [selectedVehiclesById],
  );

  const selectedIds = useMemo(
    () => selectedVehicles.map((vehicle) => vehicle.id),
    [selectedVehicles],
  );

  const handleGenerate = useCallback(async () => {
    if (!salespersonId || selectedVehicles.length === 0) return;

    try {
      const result = await generateShareLink({
        variables: {
          input: {
            institutionId,
            salespersonUserId: salespersonId,
            vehicleIds: selectedIds,
          },
        },
      });

      const url = result.data?.externalIntegrations.inv360GenerateShareLink.url;
      if (url) {
        editor.focus();
        editor.update(() => {
          const selection = $getSelection() ?? $getRoot().selectEnd();
          selection.insertText(url);
        });
      }

      showNotification({
        title: t('conversations:messageInput.options.inv360.linkGenerated'),
        type: 'success',
      });

      handleClose();
    } catch {
      showNotification({
        title: t('conversations:messageInput.options.inv360.errors.generateFailed'),
        type: 'error',
      });
    }
  }, [
    editor,
    generateShareLink,
    handleClose,
    institutionId,
    salespersonId,
    selectedIds,
    selectedVehicles.length,
    showNotification,
    t,
  ]);

  if (!enabledData?.externalIntegrations.inv360CompanyEnabled) {
    return null;
  }

  const drawerTitle = t(
    breakpointUpMd
      ? 'conversations:messageInput.options.inv360.title'
      : 'conversations:messageInput.options.inv360.titleMobile',
  );

  const drawerContent =
    step === 'picker' ? (
      <Inv360VehiclePicker
        institutionId={institutionId}
        search={search}
        setSearch={setSearch}
        sort={sort}
        setSort={setSort}
        selectedIds={selectedIds}
        toggleSelected={toggleSelected}
        selectedVehiclesById={selectedVehiclesById}
        onNext={() => setStep('configure')}
      />
    ) : (
      <Inv360ConfigureStep
        salespersonOptions={salespersonOptions}
        selectedVehicles={selectedVehicles}
        removeVehicle={removeVehicle}
        goToPicker={() => setStep('picker')}
        salespersonId={salespersonId}
        setSalespersonId={handleSalespersonChange}
        rememberSalesperson={rememberSalesperson}
        setRememberSalesperson={(checked) =>
          handleRememberSalespersonChange(checked, salespersonId)
        }
        onGenerate={() => void handleGenerate()}
        isGenerating={isGenerating}
      />
    );

  return (
    <>
      <PluginIcon
        tooltip={drawerTitle}
        type="inv360"
        lggTestId="inv360-widget-trigger"
        disabled={disabled}
        isOpen={visibility.visible}
        onClick={!disabled ? visibility.show : undefined}
      />
      {breakpointUpMd ? (
        <RightDrawer
          visible={visibility.visible}
          onClose={handleClose}
          title={drawerTitle}
          addScrollbarToBody={false}
        >
          {drawerContent}
        </RightDrawer>
      ) : (
        <BottomDrawer
          visible={visibility.visible}
          onClose={handleClose}
          title={drawerTitle}
          fullHeight
        >
          {drawerContent}
        </BottomDrawer>
      )}
    </>
  );
});
